import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { 
  Users, 
  UserCheck,
  Building,
  Building2,
  TrendingDown,
  Plus, 
  Trash2, 
  History,
  Loader2,
  Save,
  ChevronDown,
  ChevronRight,
  IndianRupee,
  MessageSquareWarning,
  AlertTriangle
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate reporting year options
const generateReportingYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < 5; i++) {
    const startYear = currentYear - i;
    years.push(`FY ${startYear}-${startYear + 1}`);
  }
  return years;
};

// Calculate previous FY from current (handles "FY 2025-2026" format)
const getPreviousFY = (currentFY) => {
  const match = currentFY.match(/(\d{4})-(\d{4})/);
  if (match) {
    const startYear = parseInt(match[1]);
    return `FY ${startYear - 1}-${startYear}`;
  }
  return currentFY;
};

// Default structures
const DEFAULT_EMPLOYEE_DETAILS = {
  permanent_male_employees: 0, permanent_female_employees: 0,
  other_than_permanent_male_employees: 0, other_than_permanent_female_employees: 0,
  diff_abled_permanent_male_employees: 0, diff_abled_permanent_female_employees: 0,
  diff_abled_other_permanent_male_employees: 0, diff_abled_other_permanent_female_employees: 0,
  permanent_male_workers: 0, permanent_female_workers: 0,
  other_than_permanent_male_workers: 0, other_than_permanent_female_workers: 0,
  diff_abled_permanent_male_workers: 0, diff_abled_permanent_female_workers: 0,
  diff_abled_other_permanent_male_workers: 0, diff_abled_other_permanent_female_workers: 0,
};

const DEFAULT_CSR = { is_applicable: false, turnover_inr: 0, net_worth_inr: 0 };

const DEFAULT_TURNOVER_RATE = {
  permanent_employees_male: 0, permanent_employees_female: 0,
  permanent_workers_male: 0, permanent_workers_female: 0,
};

const mapTurnoverPeriod = (period = {}) => ({
  permanent_employees_male: period.permanent_employees?.male || 0,
  permanent_employees_female: period.permanent_employees?.female || 0,
  permanent_workers_male: period.permanent_workers?.male || 0,
  permanent_workers_female: period.permanent_workers?.female || 0,
});

const FIXED_WOMEN_ROWS = [
  { key: 'board_of_directors', category: 'Board of Directors' },
  { key: 'key_management_personnel', category: 'Key Management Personnel' },
];
const ENTITY_TYPES = ["Holding Company", "Subsidiary", "Associate Company", "Joint Venture"];

// Complaints/Grievances fixed categories
const GRIEVANCE_CATEGORIES = [
  "Communities",
  "Investors (other than shareholders)",
  "Shareholders",
  "Employees and workers",
  "Customers",
  "Value Chain Partners",
  "Other"
];

const DEFAULT_GRIEVANCE_ROW = {
  category: "Communities",
  has_grievance_mechanism: false,
  policy_weblink: "",
  current_fy_filed: 0,
  current_fy_pending: 0,
  current_fy_remarks: "",
  previous_fy_filed: 0,
  previous_fy_pending: 0,
  previous_fy_remarks: ""
};

const DEFAULT_MATERIAL_ISSUE_ROW = {
  issue_identified: "",
  risk_or_opportunity: "Risk",
  rationale: "",
  mitigation_approach: "",
  financial_implication: "Neutral",
  financial_details: ""
};

const formatINR = (num) => num ? '₹' + num.toLocaleString('en-IN') : '₹0';

const normalizeWomenRows = (rows = []) => FIXED_WOMEN_ROWS.map((fixed) => {
  const rowList = Array.isArray(rows) ? rows : [];
  const existing = rowList.find((row) => row?.key === fixed.key || row?.category === fixed.category) || {};
  return {
    key: fixed.key,
    category: fixed.category,
    total: Number(existing.total) || 0,
    number_of_females: Number(existing.number_of_females) || 0,
  };
});

const getNestedValue = (value, path) => path.split('.').reduce(
  (current, part) => (current && typeof current === 'object' ? current[part] : undefined), value
);

const mergeNestedValues = (baseValue, liveValue) => {
  if (liveValue === undefined) return baseValue;
  if (!liveValue || typeof liveValue !== 'object' || Array.isArray(liveValue)) return liveValue;
  const base = baseValue && typeof baseValue === 'object' && !Array.isArray(baseValue) ? baseValue : {};
  return Object.entries(liveValue).reduce((merged, [key, value]) => ({
    ...merged,
    [key]: value && typeof value === 'object' && !Array.isArray(value)
      ? mergeNestedValues(merged[key], value)
      : value,
  }), { ...base });
};

const mergeWomenRepresentation = (savedRows, liveValue) => {
  const savedRowList = Array.isArray(savedRows) ? savedRows
    : Object.entries(savedRows?.rows || savedRows || {}).map(([key, row]) => ({ key, ...(row || {}) }));
  const liveRows = Array.isArray(liveValue) ? liveValue.reduce((rows, row) => ({ ...rows, [row?.key]: row }), {})
    : (liveValue?.rows || liveValue || {});
  return normalizeWomenRows(savedRowList).map((row) => {
    const liveRow = liveRows[row.key];
    const merged = liveRow && typeof liveRow === 'object' ? { ...row, ...liveRow } : row;
    const total = Number(merged.total) || 0;
    const numberOfFemales = Number(merged.number_of_females) || 0;
    return { ...merged, total, number_of_females: numberOfFemales };
  });
};

const EMPLOYEE_WORKFORCE_FIELD_PATHS = {
  permanent_male_employees: 'employees.permanent.male',
  permanent_female_employees: 'employees.permanent.female',
  other_than_permanent_male_employees: 'employees.other_than_permanent.male',
  other_than_permanent_female_employees: 'employees.other_than_permanent.female',
  permanent_male_workers: 'workers.permanent.male',
  permanent_female_workers: 'workers.permanent.female',
  other_than_permanent_male_workers: 'workers.other_than_permanent.male',
  other_than_permanent_female_workers: 'workers.other_than_permanent.female',
};

const DIFFERENTLY_ABLED_FIELD_PATHS = {
  diff_abled_permanent_male_employees: 'employees.permanent.male',
  diff_abled_permanent_female_employees: 'employees.permanent.female',
  diff_abled_other_permanent_male_employees: 'employees.other_than_permanent.male',
  diff_abled_other_permanent_female_employees: 'employees.other_than_permanent.female',
  diff_abled_permanent_male_workers: 'workers.permanent.male',
  diff_abled_permanent_female_workers: 'workers.permanent.female',
  diff_abled_other_permanent_male_workers: 'workers.other_than_permanent.male',
  diff_abled_other_permanent_female_workers: 'workers.other_than_permanent.female',
};

const WORKFORCE_FIELD_PATHS_BY_QUESTION = {
  brsr_a_employees_workers: EMPLOYEE_WORKFORCE_FIELD_PATHS,
  brsr_a_differently_abled: DIFFERENTLY_ABLED_FIELD_PATHS,
};

const compositionPercentage = (part, total) => total > 0 ? `${((part / total) * 100).toFixed(2)}%` : '0.00%';

const WorkforceCompositionTable = ({ title, groups, details, setDetails, editable, lockedFields = new Set(), testId }) => {
  const renderNumber = (field, inputTestId) => editable && !lockedFields.has(field) ? (
    <Input
      type="number"
      min="0"
      value={details[field] || 0}
      onChange={(event) => setDetails((previous) => ({ ...previous, [field]: parseInt(event.target.value, 10) || 0 }))}
      className="h-8 min-w-16 text-center text-xs"
      data-testid={inputTestId}
    />
  ) : <span className="tabular-nums" data-testid={lockedFields.has(field) ? `${inputTestId}-kpi-autofill` : undefined}>{details[field] || 0}</span>;

  return (
    <div className="space-y-2" data-testid={testId}>
      <h5 className="text-sm font-semibold text-stone-800">{title}</h5>
      <div className="overflow-x-auto border border-stone-200">
        <Table className="min-w-[760px] table-fixed">
          <TableHeader>
            <TableRow className="bg-stone-100">
              <TableHead rowSpan={2} className="w-14 border-r text-center text-xs">S. No.</TableHead>
              <TableHead rowSpan={2} className="w-[30%] border-r text-xs">Particulars</TableHead>
              <TableHead rowSpan={2} className="w-24 border-r text-center text-xs">Total (A)</TableHead>
              <TableHead colSpan={2} className="border-r text-center text-xs">Male</TableHead>
              <TableHead colSpan={2} className="text-center text-xs">Female</TableHead>
            </TableRow>
            <TableRow className="bg-stone-50">
              <TableHead className="border-r text-center text-xs">No. (B)</TableHead>
              <TableHead className="border-r text-center text-xs">% (B/A)</TableHead>
              <TableHead className="border-r text-center text-xs">No. (C)</TableHead>
              <TableHead className="text-center text-xs">% (C/A)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.flatMap((group) => {
              const detailRows = group.rows.map((row) => {
                const male = details[row.maleField] || 0;
                const female = details[row.femaleField] || 0;
                const total = male + female;
                return (
                  <TableRow key={row.key}>
                    <TableCell className="border-r text-center text-xs">{row.serial}</TableCell>
                    <TableCell className="border-r text-xs">{row.label}</TableCell>
                    <TableCell className="border-r text-center text-xs font-medium tabular-nums">{total}</TableCell>
                    <TableCell className="border-r p-1 text-center text-xs">{renderNumber(row.maleField, `${testId}-${row.key}-male`)}</TableCell>
                    <TableCell className="border-r text-center text-xs tabular-nums">{compositionPercentage(male, total)}</TableCell>
                    <TableCell className="border-r p-1 text-center text-xs">{renderNumber(row.femaleField, `${testId}-${row.key}-female`)}</TableCell>
                    <TableCell className="text-center text-xs tabular-nums">{compositionPercentage(female, total)}</TableCell>
                  </TableRow>
                );
              });
              const totalMale = group.rows.reduce((sum, row) => sum + (details[row.maleField] || 0), 0);
              const totalFemale = group.rows.reduce((sum, row) => sum + (details[row.femaleField] || 0), 0);
              const total = totalMale + totalFemale;
              return [
                <TableRow key={`${group.key}-heading`} className="bg-emerald-50/60">
                  <TableCell colSpan={7} className="text-xs font-bold uppercase text-emerald-900">{group.heading}</TableCell>
                </TableRow>,
                ...detailRows,
                <TableRow key={`${group.key}-total`} className="bg-stone-50 font-semibold">
                  <TableCell className="border-r text-center text-xs">{group.totalSerial}</TableCell>
                  <TableCell className="border-r text-xs">{group.totalLabel}</TableCell>
                  <TableCell className="border-r text-center text-xs tabular-nums">{total}</TableCell>
                  <TableCell className="border-r text-center text-xs tabular-nums">{totalMale}</TableCell>
                  <TableCell className="border-r text-center text-xs tabular-nums">{compositionPercentage(totalMale, total)}</TableCell>
                  <TableCell className="border-r text-center text-xs tabular-nums">{totalFemale}</TableCell>
                  <TableCell className="text-center text-xs tabular-nums">{compositionPercentage(totalFemale, total)}</TableCell>
                </TableRow>,
              ];
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const WORKFORCE_GROUPS = [
  {
    key: 'employees', heading: 'Employees', totalSerial: 3, totalLabel: 'Total employees (D + E)',
    rows: [
      { key: 'employees-permanent', serial: 1, label: 'Permanent (D)', maleField: 'permanent_male_employees', femaleField: 'permanent_female_employees' },
      { key: 'employees-other', serial: 2, label: 'Other than Permanent (E)', maleField: 'other_than_permanent_male_employees', femaleField: 'other_than_permanent_female_employees' },
    ],
  },
  {
    key: 'workers', heading: 'Workers', totalSerial: 6, totalLabel: 'Total workers (F + G)',
    rows: [
      { key: 'workers-permanent', serial: 4, label: 'Permanent (F)', maleField: 'permanent_male_workers', femaleField: 'permanent_female_workers' },
      { key: 'workers-other', serial: 5, label: 'Other than Permanent (G)', maleField: 'other_than_permanent_male_workers', femaleField: 'other_than_permanent_female_workers' },
    ],
  },
];

const DIFFERENTLY_ABLED_GROUPS = [
  {
    key: 'differently-abled-employees', heading: 'Differently Abled Employees', totalSerial: 3,
    totalLabel: 'Total differently abled employees (D + E)',
    rows: [
      { key: 'differently-abled-employees-permanent', serial: 1, label: 'Permanent (D)', maleField: 'diff_abled_permanent_male_employees', femaleField: 'diff_abled_permanent_female_employees' },
      { key: 'differently-abled-employees-other', serial: 2, label: 'Other than Permanent (E)', maleField: 'diff_abled_other_permanent_male_employees', femaleField: 'diff_abled_other_permanent_female_employees' },
    ],
  },
  {
    key: 'differently-abled-workers', heading: 'Differently Abled Workers', totalSerial: 6,
    totalLabel: 'Total differently abled workers (F + G)',
    rows: [
      { key: 'differently-abled-workers-permanent', serial: 4, label: 'Permanent (F)', maleField: 'diff_abled_permanent_male_workers', femaleField: 'diff_abled_permanent_female_workers' },
      { key: 'differently-abled-workers-other', serial: 5, label: 'Other than Permanent (G)', maleField: 'diff_abled_other_permanent_male_workers', femaleField: 'diff_abled_other_permanent_female_workers' },
    ],
  },
];

export default function BRSRYearlySections({ isEditing = false, hideSections = [], reportingYear: propReportingYear = '', assignedQuestionKeys = null, isAdmin = false, liveKPIAutofillByQuestion = {} }) {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Use prop if provided, otherwise use default
  const [reportingYear, setReportingYear] = useState(propReportingYear || generateReportingYears()[0]);
  const [availableYears, setAvailableYears] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);
  
  // Update reportingYear when prop changes
  useEffect(() => {
    if (propReportingYear && propReportingYear !== reportingYear) {
      setReportingYear(propReportingYear);
    }
  }, [propReportingYear]);

  // Check if user can see/edit a specific question
  const canSeeQuestion = (questionKey) => {
    if (isAdmin) return true;
    if (assignedQuestionKeys === null) return false;
    if (assignedQuestionKeys.length === 0) return false;
    return assignedQuestionKeys.includes(questionKey);
  };
  
  const canEditQuestion = (questionKey) => {
    if (isAdmin) return true;
    if (assignedQuestionKeys === null) return false;
    return assignedQuestionKeys.includes(questionKey);
  };
  
  // Section open states
  const [openSections, setOpenSections] = useState({
    employees: false, women: false, csr: false, holding: false, turnover: false, complaints: false, materialIssues: false
  });

  // Form data
  const [employeeDetails, setEmployeeDetails] = useState({ ...DEFAULT_EMPLOYEE_DETAILS });
  const [womenRepresentation, setWomenRepresentation] = useState(normalizeWomenRows());
  const [csrApplicability, setCSRApplicability] = useState({ ...DEFAULT_CSR });
  const [holdingEntities, setHoldingEntities] = useState([
    { name_of_entity: "", type_of_entity: "Subsidiary", shares_held_percentage: 0, participates_in_br_initiatives: false }
  ]);
  // The active BRSR response carries the complete three-year turnover matrix.
  const [turnoverRate, setTurnoverRate] = useState({ ...DEFAULT_TURNOVER_RATE });
  // Previous-period values belong to the active reporting response, not separate reports.
  const [prevYearTurnover, setPrevYearTurnover] = useState({ ...DEFAULT_TURNOVER_RATE });
  const [priorYearTurnover, setPriorYearTurnover] = useState({ ...DEFAULT_TURNOVER_RATE });
  // Complaints/Grievances - initialize with all fixed categories
  const [complaintsGrievances, setComplaintsGrievances] = useState(
    GRIEVANCE_CATEGORIES.map(cat => ({ ...DEFAULT_GRIEVANCE_ROW, category: cat }))
  );
  // Material Issues
  const [materialIssues, setMaterialIssues] = useState([{ ...DEFAULT_MATERIAL_ISSUE_ROW }]);

  const liveQuestionValue = (questionKey) => liveKPIAutofillByQuestion[questionKey]?.value;
  const lockedPathsFor = (questionKey) => new Set(
    (liveKPIAutofillByQuestion[questionKey]?.locked_paths || []).map((path) => path.replace(/^rows\./, ''))
  );
  const workforceLockedFields = (questionKey) => new Set(
    Object.entries(WORKFORCE_FIELD_PATHS_BY_QUESTION[questionKey] || {})
      .filter(([, path]) => lockedPathsFor(questionKey).has(path))
      .map(([field]) => field)
  );
  const employeeLockedFields = workforceLockedFields('brsr_a_employees_workers');
  const differentlyAbledLockedFields = workforceLockedFields('brsr_a_differently_abled');
  const womenLockedPaths = lockedPathsFor('brsr_a_women_representation');

  useEffect(() => {
    const applyWorkforceAutofill = (previous) => {
      const next = { ...previous };
      [
        ['brsr_a_employees_workers', employeeLockedFields],
        ['brsr_a_differently_abled', differentlyAbledLockedFields],
      ].forEach(([questionKey, lockedFields]) => {
        const liveValue = liveQuestionValue(questionKey);
        const fieldPaths = WORKFORCE_FIELD_PATHS_BY_QUESTION[questionKey] || {};
        lockedFields.forEach((field) => {
          const value = getNestedValue(liveValue, fieldPaths[field]);
          if (value !== undefined) next[field] = Number(value) || 0;
        });
      });
      return next;
    };
    setEmployeeDetails(applyWorkforceAutofill);
    setWomenRepresentation((previous) => mergeWomenRepresentation(
      previous,
      liveQuestionValue('brsr_a_women_representation')
    ));
  }, [liveKPIAutofillByQuestion]);

  useEffect(() => {
    fetchYearlyData();
  }, [reportingYear]);

  // Map ESG responses to yearly section form data
  const mapResponsesToFormData = (responses) => {
    // Employee/worker details
    const employeeWorkerValues = mergeNestedValues(
      responses.brsr_a_employees_workers,
      liveQuestionValue('brsr_a_employees_workers')
    );
    if (employeeWorkerValues) {
      const ew = employeeWorkerValues;
      setEmployeeDetails({
        permanent_male_employees: ew.employees?.permanent?.male || 0,
        permanent_female_employees: ew.employees?.permanent?.female || 0,
        other_than_permanent_male_employees: ew.employees?.other_than_permanent?.male || 0,
        other_than_permanent_female_employees: ew.employees?.other_than_permanent?.female || 0,
        permanent_male_workers: ew.workers?.permanent?.male || 0,
        permanent_female_workers: ew.workers?.permanent?.female || 0,
        other_than_permanent_male_workers: ew.workers?.other_than_permanent?.male || 0,
        other_than_permanent_female_workers: ew.workers?.other_than_permanent?.female || 0,
        diff_abled_permanent_male_employees: 0,
        diff_abled_permanent_female_employees: 0,
        diff_abled_other_permanent_male_employees: 0,
        diff_abled_other_permanent_female_employees: 0,
        diff_abled_permanent_male_workers: 0,
        diff_abled_permanent_female_workers: 0,
        diff_abled_other_permanent_male_workers: 0,
        diff_abled_other_permanent_female_workers: 0,
      });
    }
    
    // Differently abled
    const differentlyAbledValues = mergeNestedValues(
      responses.brsr_a_differently_abled,
      liveQuestionValue('brsr_a_differently_abled')
    );
    if (differentlyAbledValues) {
      const da = differentlyAbledValues;
      setEmployeeDetails(prev => ({
        ...prev,
        diff_abled_permanent_male_employees: da.employees?.permanent?.male || 0,
        diff_abled_permanent_female_employees: da.employees?.permanent?.female || 0,
        diff_abled_other_permanent_male_employees: da.employees?.other_than_permanent?.male || 0,
        diff_abled_other_permanent_female_employees: da.employees?.other_than_permanent?.female || 0,
        diff_abled_permanent_male_workers: da.workers?.permanent?.male || 0,
        diff_abled_permanent_female_workers: da.workers?.permanent?.female || 0,
        diff_abled_other_permanent_male_workers: da.workers?.other_than_permanent?.male || 0,
        diff_abled_other_permanent_female_workers: da.workers?.other_than_permanent?.female || 0,
      }));
    }
    
    // Women representation
    setWomenRepresentation(mergeWomenRepresentation(
      responses.brsr_a_women_representation,
      liveQuestionValue('brsr_a_women_representation')
    ));
    
    // CSR
    if (responses.brsr_a_csr_applicability) {
      setCSRApplicability({ ...DEFAULT_CSR, ...responses.brsr_a_csr_applicability });
    } else {
      setCSRApplicability({ ...DEFAULT_CSR });
    }
    
    // Holding entities
    if (responses.brsr_a_holding_subsidiary?.length > 0) {
      setHoldingEntities(responses.brsr_a_holding_subsidiary);
    } else {
      setHoldingEntities([{ name_of_entity: "", type_of_entity: "Subsidiary", shares_held_percentage: 0, participates_in_br_initiatives: false }]);
    }
    
    // Turnover rate: live autofill returns all three periods in the active report.
    const turnoverValues = mergeNestedValues(
      responses.brsr_a_turnover_rate,
      liveQuestionValue('brsr_a_turnover_rate')
    );
    if (turnoverValues) {
      setTurnoverRate(mapTurnoverPeriod(turnoverValues.current_fy));
      setPrevYearTurnover(mapTurnoverPeriod(turnoverValues.previous_fy));
      setPriorYearTurnover(mapTurnoverPeriod(
        turnoverValues.prior_to_previous_fy || turnoverValues.prior_fy
      ));
    } else {
      setTurnoverRate({ ...DEFAULT_TURNOVER_RATE });
      setPrevYearTurnover({ ...DEFAULT_TURNOVER_RATE });
      setPriorYearTurnover({ ...DEFAULT_TURNOVER_RATE });
    }
    
    // Complaints/Grievances
    if (responses.brsr_a_complaints_grievances?.length > 0) {
      const merged = GRIEVANCE_CATEGORIES.map(cat => {
        const existing = responses.brsr_a_complaints_grievances.find(c => c.category === cat);
        return existing || { ...DEFAULT_GRIEVANCE_ROW, category: cat };
      });
      setComplaintsGrievances(merged);
    } else {
      setComplaintsGrievances(GRIEVANCE_CATEGORIES.map(cat => ({ ...DEFAULT_GRIEVANCE_ROW, category: cat })));
    }
    
    // Material Issues
    if (responses.brsr_a_material_issues?.length > 0) {
      setMaterialIssues(responses.brsr_a_material_issues);
    } else {
      setMaterialIssues([{ ...DEFAULT_MATERIAL_ISSUE_ROW }]);
    }
  };

  // Map form data to ESG responses format
  const mapFormDataToResponses = () => {
    return {
      brsr_a_employees_workers: {
        employees: {
          permanent: {
            male: employeeDetails.permanent_male_employees,
            female: employeeDetails.permanent_female_employees,
            total: employeeDetails.permanent_male_employees + employeeDetails.permanent_female_employees,
          },
          other_than_permanent: {
            male: employeeDetails.other_than_permanent_male_employees,
            female: employeeDetails.other_than_permanent_female_employees,
            total: employeeDetails.other_than_permanent_male_employees + employeeDetails.other_than_permanent_female_employees,
          },
        },
        workers: {
          permanent: {
            male: employeeDetails.permanent_male_workers,
            female: employeeDetails.permanent_female_workers,
            total: employeeDetails.permanent_male_workers + employeeDetails.permanent_female_workers,
          },
          other_than_permanent: {
            male: employeeDetails.other_than_permanent_male_workers,
            female: employeeDetails.other_than_permanent_female_workers,
            total: employeeDetails.other_than_permanent_male_workers + employeeDetails.other_than_permanent_female_workers,
          },
        },
      },
      brsr_a_differently_abled: {
        employees: {
          permanent: {
            male: employeeDetails.diff_abled_permanent_male_employees,
            female: employeeDetails.diff_abled_permanent_female_employees,
            total: employeeDetails.diff_abled_permanent_male_employees + employeeDetails.diff_abled_permanent_female_employees,
          },
          other_than_permanent: {
            male: employeeDetails.diff_abled_other_permanent_male_employees,
            female: employeeDetails.diff_abled_other_permanent_female_employees,
            total: employeeDetails.diff_abled_other_permanent_male_employees + employeeDetails.diff_abled_other_permanent_female_employees,
          },
        },
        workers: {
          permanent: {
            male: employeeDetails.diff_abled_permanent_male_workers,
            female: employeeDetails.diff_abled_permanent_female_workers,
            total: employeeDetails.diff_abled_permanent_male_workers + employeeDetails.diff_abled_permanent_female_workers,
          },
          other_than_permanent: {
            male: employeeDetails.diff_abled_other_permanent_male_workers,
            female: employeeDetails.diff_abled_other_permanent_female_workers,
            total: employeeDetails.diff_abled_other_permanent_male_workers + employeeDetails.diff_abled_other_permanent_female_workers,
          },
        },
      },
      brsr_a_women_representation: womenRepresentation.map((row) => ({
        ...row,
        percentage: row.total > 0 ? Number(((row.number_of_females / row.total) * 100).toFixed(2)) : 0,
      })),
      brsr_a_csr_applicability: csrApplicability,
      brsr_a_holding_subsidiary: holdingEntities,
      brsr_a_turnover_rate: {
        current_fy: {
          permanent_employees: {
            male: turnoverRate.permanent_employees_male,
            female: turnoverRate.permanent_employees_female,
            total: turnoverRate.permanent_employees_male + turnoverRate.permanent_employees_female,
          },
          permanent_workers: {
            male: turnoverRate.permanent_workers_male,
            female: turnoverRate.permanent_workers_female,
            total: turnoverRate.permanent_workers_male + turnoverRate.permanent_workers_female,
          },
        },
      },
      brsr_a_complaints_grievances: complaintsGrievances,
      brsr_a_material_issues: materialIssues,
    };
  };

  const fetchYearlyData = async () => {
    setLoading(true);
    try {
      // Fetch from ESG Questionnaire API (unified storage)
      const res = await axios.get(
        `${API}/esg-questionnaire/responses/BRSR/section_a/${encodeURIComponent(reportingYear)}`,
        { headers: getAuthHeader() }
      );
      
      if (res.data.responses && Object.keys(res.data.responses).length > 0) {
        mapResponsesToFormData(res.data.responses);
      } else {
        resetToDefaults();
      }
      
      // Fetch available years (keep using old endpoint for now, will refactor later)
      try {
        const yearsRes = await axios.get(`${API}/organizations/my/framework-details/brsr/yearly`, { headers: getAuthHeader() });
        setAvailableYears(yearsRes.data.available_years || []);
      } catch (e) {
        setAvailableYears(generateReportingYears());
      }
      
    } catch (error) {
      if (error.response?.status !== 404) console.error('Fetch error:', error);
      resetToDefaults();
    } finally {
      setLoading(false);
    }
  };

  const resetToDefaults = () => {
    setEmployeeDetails({ ...DEFAULT_EMPLOYEE_DETAILS });
    setWomenRepresentation(normalizeWomenRows());
    setCSRApplicability({ ...DEFAULT_CSR });
    setHoldingEntities([{ name_of_entity: "", type_of_entity: "Subsidiary", shares_held_percentage: 0, participates_in_br_initiatives: false }]);
    setTurnoverRate({ ...DEFAULT_TURNOVER_RATE });
    setPrevYearTurnover({ ...DEFAULT_TURNOVER_RATE });
    setPriorYearTurnover({ ...DEFAULT_TURNOVER_RATE });
    setComplaintsGrievances(GRIEVANCE_CATEGORIES.map(cat => ({ ...DEFAULT_GRIEVANCE_ROW, category: cat })));
    setMaterialIssues([{ ...DEFAULT_MATERIAL_ISSUE_ROW }]);
  };

  const saveAllYearlyData = async () => {
    setSaving(true);
    const prevFY = getPreviousFY(reportingYear);
    const priorFY = getPreviousFY(prevFY);
    
    try {
      // Convert form data to ESG responses format
      const currentResponses = mapFormDataToResponses();

      // Save current year data via ESG Questionnaire API
      await axios.put(
        `${API}/esg-questionnaire/responses/BRSR/section_a/${encodeURIComponent(reportingYear)}`,
        { responses: currentResponses },
        { headers: getAuthHeader() }
      );
      
      // Save previous years' turnover data (only if changed)
      const prevYearResponses = {
        brsr_a_turnover_rate: {
          current_fy: {
            permanent_employees: {
              male: prevYearTurnover.permanent_employees_male,
              female: prevYearTurnover.permanent_employees_female,
              total: prevYearTurnover.permanent_employees_male + prevYearTurnover.permanent_employees_female,
            },
            permanent_workers: {
              male: prevYearTurnover.permanent_workers_male,
              female: prevYearTurnover.permanent_workers_female,
              total: prevYearTurnover.permanent_workers_male + prevYearTurnover.permanent_workers_female,
            },
          },
        },
      };
      
      const priorYearResponses = {
        brsr_a_turnover_rate: {
          current_fy: {
            permanent_employees: {
              male: priorYearTurnover.permanent_employees_male,
              female: priorYearTurnover.permanent_employees_female,
              total: priorYearTurnover.permanent_employees_male + priorYearTurnover.permanent_employees_female,
            },
            permanent_workers: {
              male: priorYearTurnover.permanent_workers_male,
              female: priorYearTurnover.permanent_workers_female,
              total: priorYearTurnover.permanent_workers_male + priorYearTurnover.permanent_workers_female,
            },
          },
        },
      };
      
      await Promise.all([
        axios.put(
          `${API}/esg-questionnaire/responses/BRSR/section_a/${encodeURIComponent(prevFY)}`,
          { responses: prevYearResponses },
          { headers: getAuthHeader() }
        ),
        axios.put(
          `${API}/esg-questionnaire/responses/BRSR/section_a/${encodeURIComponent(priorFY)}`,
          { responses: priorYearResponses },
          { headers: getAuthHeader() }
        )
      ]);

      toast.success(`Section A data saved for ${reportingYear}, ${prevFY}, and ${priorFY}`);
      fetchYearlyData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save yearly data');
    } finally {
      setSaving(false);
    }
  };

  const fetchHistoricalData = async () => {
    try {
      const res = await axios.get(`${API}/organizations/my/framework-details/brsr/yearly`, { headers: getAuthHeader() });
      setHistoricalData(res.data.yearly_data || []);
    } catch (error) {
      console.error('History fetch error:', error);
    }
  };

  const toggleSection = (section) => setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }

  const prevFY = getPreviousFY(reportingYear);
  const priorFY = getPreviousFY(prevFY);

  // Check if user has ANY yearly section assigned
  const hasAnyYearlyAssignment = isAdmin || (assignedQuestionKeys && assignedQuestionKeys.some(k => 
    ['brsr_a_employees_workers', 'brsr_a_differently_abled', 'brsr_a_women_representation', 
     'brsr_a_csr_applicability', 'brsr_a_holding_subsidiary', 'brsr_a_turnover_rate',
     'brsr_a_complaints_grievances', 'brsr_a_material_issues'].includes(k)
  ));

  // If no yearly assignments and not admin, show nothing
  if (!hasAnyYearlyAssignment) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* 1. Employee & Worker Details */}
      {!hideSections.includes('employees_workers') && (isAdmin || canSeeQuestion('brsr_a_employees_workers') || canSeeQuestion('brsr_a_differently_abled')) && (
      <Collapsible open={openSections.employees} onOpenChange={() => toggleSection('employees')} className="border rounded-lg bg-white">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-3 hover:bg-stone-50">
            <div className="flex items-center gap-2">
              {openSections.employees ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Users className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">Details of Employees and Workers (including differently abled)</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {(employeeDetails.permanent_male_employees || 0) + (employeeDetails.permanent_female_employees || 0) + 
               (employeeDetails.other_than_permanent_male_employees || 0) + (employeeDetails.other_than_permanent_female_employees || 0) +
               (employeeDetails.permanent_male_workers || 0) + (employeeDetails.permanent_female_workers || 0) +
               (employeeDetails.other_than_permanent_male_workers || 0) + (employeeDetails.other_than_permanent_female_workers || 0)} Total
            </Badge>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-6 border-t p-4">
          {(isAdmin || canSeeQuestion('brsr_a_employees_workers')) && (
            <WorkforceCompositionTable
              title="a. Employees and workers (including differently abled)"
              groups={WORKFORCE_GROUPS}
              details={employeeDetails}
              setDetails={setEmployeeDetails}
              editable={isEditing && canEditQuestion('brsr_a_employees_workers')}
              lockedFields={employeeLockedFields}
              testId="brsr-employees-workers-table"
            />
          )}
          {(isAdmin || canSeeQuestion('brsr_a_differently_abled')) && (
            <WorkforceCompositionTable
              title="b. Differently abled Employees and workers"
              groups={DIFFERENTLY_ABLED_GROUPS}
              details={employeeDetails}
              setDetails={setEmployeeDetails}
              editable={isEditing && canEditQuestion('brsr_a_differently_abled')}
              lockedFields={differentlyAbledLockedFields}
              testId="brsr-differently-abled-table"
            />
          )}
        </CollapsibleContent>
      </Collapsible>
      )}

      {/* 2. Women Representation */}
      {!hideSections.includes('women_representation') && (isAdmin || canSeeQuestion('brsr_a_women_representation')) && (
      <Collapsible open={openSections.women} onOpenChange={() => toggleSection('women')} className="border rounded-lg bg-white">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-3 hover:bg-stone-50">
            <div className="flex items-center gap-2">
              {openSections.women ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <UserCheck className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">Participation/Inclusion/Representation of women</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {womenRepresentation.reduce((sum, row) => sum + (row.number_of_females || 0), 0)} Women
            </Badge>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="p-4 pt-0 border-t">
          <Table className="table-fixed" data-testid="brsr-women-representation-table">
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="w-[40%] text-xs"></TableHead>
                <TableHead className="w-[20%] text-center text-xs">Total (A)</TableHead>
                <TableHead className="w-[20%] text-center text-xs">No. of Females (B)</TableHead>
                <TableHead className="w-[20%] text-center text-xs">% (B/A)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {womenRepresentation.map((row, idx) => (
                <TableRow key={row.key} data-testid={`brsr-women-row-${row.key}`}>
                  <TableCell className="text-xs font-medium">{row.category}</TableCell>
                  <TableCell>
                    {isEditing && canEditQuestion('brsr_a_women_representation') && !womenLockedPaths.has(`${row.key}.total`) ? (
                      <Input type="number" min="0" value={row.total} onChange={(e) => {
                        const updated = [...womenRepresentation]; updated[idx].total = parseInt(e.target.value) || 0; setWomenRepresentation(updated);
                      }} className="h-8 text-center text-xs" data-testid={`brsr-women-${row.key}-total`} />
                    ) : <div className="text-center text-xs tabular-nums" data-testid={womenLockedPaths.has(`${row.key}.total`) ? `brsr-women-${row.key}-total-kpi-autofill` : undefined}>{row.total}</div>}
                  </TableCell>
                  <TableCell>
                    {isEditing && canEditQuestion('brsr_a_women_representation') && !womenLockedPaths.has(`${row.key}.number_of_females`) ? (
                      <Input type="number" min="0" value={row.number_of_females} onChange={(e) => {
                        const updated = [...womenRepresentation]; updated[idx].number_of_females = parseInt(e.target.value) || 0; setWomenRepresentation(updated);
                      }} className="h-8 text-center text-xs" data-testid={`brsr-women-${row.key}-female`} />
                    ) : <div className="text-center text-xs tabular-nums" data-testid={womenLockedPaths.has(`${row.key}.number_of_females`) ? `brsr-women-${row.key}-female-kpi-autofill` : undefined}>{row.number_of_females}</div>}
                  </TableCell>
                  <TableCell className="text-center text-xs font-medium tabular-nums" data-testid={`brsr-women-${row.key}-percentage`}>
                    {compositionPercentage(row.number_of_females, row.total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CollapsibleContent>
      </Collapsible>
      )}

      {/* 3. CSR Applicability */}
      {(isAdmin || canSeeQuestion('brsr_a_csr_applicability')) && (
      <Collapsible open={openSections.csr} onOpenChange={() => toggleSection('csr')} className="border rounded-lg bg-white">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-3 hover:bg-stone-50">
            <div className="flex items-center gap-2">
              {openSections.csr ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Building2 className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">CSR Applicability</span>
            </div>
            <Badge variant="outline" className={`text-xs ${csrApplicability.is_applicable ? 'bg-green-50 text-green-700' : ''}`}>
              {csrApplicability.is_applicable ? 'Applicable' : 'Not Applicable'}
            </Badge>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="p-4 pt-0 border-t">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 border rounded bg-stone-50">
              <Label className="text-xs">CSR under Section 135?</Label>
              {isEditing && canEditQuestion('brsr_a_csr_applicability') ? (
                <div className="flex items-center gap-2 mt-2">
                  <Switch checked={csrApplicability.is_applicable} onCheckedChange={(v) => setCSRApplicability(p => ({ ...p, is_applicable: v }))} data-testid="brsr-csr-applicability-switch" />
                  <span className="text-xs">{csrApplicability.is_applicable ? 'Yes' : 'No'}</span>
                </div>
              ) : <p className="text-sm font-medium mt-1">{csrApplicability.is_applicable ? 'Yes' : 'No'}</p>}
            </div>
            <div className="p-3 border rounded">
              <Label className="text-xs flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Turnover (INR)</Label>
              {isEditing && canEditQuestion('brsr_a_csr_applicability') ? (
                <Input type="number" min="0" value={csrApplicability.turnover_inr} onChange={(e) => setCSRApplicability(p => ({ ...p, turnover_inr: parseFloat(e.target.value) || 0 }))} className="h-8 mt-1" data-testid="brsr-csr-turnover-input" />
              ) : <p className="text-sm font-medium mt-1" data-testid="brsr-csr-turnover-value">{formatINR(csrApplicability.turnover_inr)}</p>}
            </div>
            <div className="p-3 border rounded">
              <Label className="text-xs flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Net Worth (INR)</Label>
              {isEditing && canEditQuestion('brsr_a_csr_applicability') ? (
                <Input type="number" min="0" value={csrApplicability.net_worth_inr} onChange={(e) => setCSRApplicability(p => ({ ...p, net_worth_inr: parseFloat(e.target.value) || 0 }))} className="h-8 mt-1" data-testid="brsr-csr-net-worth-input" />
              ) : <p className="text-sm font-medium mt-1" data-testid="brsr-csr-net-worth-value">{formatINR(csrApplicability.net_worth_inr)}</p>}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
      )}

      {/* 4. Holding/Subsidiary Companies */}
      {(isAdmin || canSeeQuestion('brsr_a_holding_subsidiary')) && (
      <Collapsible open={openSections.holding} onOpenChange={() => toggleSection('holding')} className="border rounded-lg bg-white">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-3 hover:bg-stone-50">
            <div className="flex items-center gap-2">
              {openSections.holding ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Building className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">Holding, Subsidiary & Associate Companies</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {holdingEntities.filter(e => e.name_of_entity).length} Entities
            </Badge>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="p-4 pt-0 border-t">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="text-xs min-w-[150px]">Entity Name</TableHead>
                  <TableHead className="text-xs min-w-[120px]">Type</TableHead>
                  <TableHead className="text-xs text-center">% Shares</TableHead>
                  <TableHead className="text-xs text-center">BR Participation</TableHead>
                  {isEditing && <TableHead className="text-xs w-12"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {holdingEntities.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {isEditing ? (
                        <Input value={row.name_of_entity} onChange={(e) => {
                          const updated = [...holdingEntities]; updated[idx].name_of_entity = e.target.value; setHoldingEntities(updated);
                        }} className="h-7 text-xs" placeholder="Entity name" />
                      ) : <span className="text-xs">{row.name_of_entity || '-'}</span>}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Select value={row.type_of_entity} onValueChange={(v) => {
                          const updated = [...holdingEntities]; updated[idx].type_of_entity = v; setHoldingEntities(updated);
                        }}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{ENTITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <span className="text-xs">{row.type_of_entity}</span>}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input type="number" min="0" max="100" value={row.shares_held_percentage} onChange={(e) => {
                          const updated = [...holdingEntities]; updated[idx].shares_held_percentage = parseFloat(e.target.value) || 0; setHoldingEntities(updated);
                        }} className="h-7 text-xs text-center" />
                      ) : <span className="text-xs">{row.shares_held_percentage}%</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      {isEditing ? (
                        <Switch checked={row.participates_in_br_initiatives} onCheckedChange={(v) => {
                          const updated = [...holdingEntities]; updated[idx].participates_in_br_initiatives = v; setHoldingEntities(updated);
                        }} />
                      ) : <Badge variant="outline" className={`text-xs ${row.participates_in_br_initiatives ? 'bg-green-50 text-green-700' : ''}`}>{row.participates_in_br_initiatives ? 'Yes' : 'No'}</Badge>}
                    </TableCell>
                    {isEditing && (
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => {
                          if (holdingEntities.length > 1) setHoldingEntities(holdingEntities.filter((_, i) => i !== idx));
                        }} className="h-6 w-6 p-0 text-red-500"><Trash2 className="w-3 h-3" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {isEditing && canEditQuestion('brsr_a_holding_subsidiary') && (
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setHoldingEntities([...holdingEntities, { name_of_entity: "", type_of_entity: "Subsidiary", shares_held_percentage: 0, participates_in_br_initiatives: false }])}>
              <Plus className="w-3 h-3 mr-1" /> Add Entity
            </Button>
          )}
        </CollapsibleContent>
      </Collapsible>
      )}

      {/* 5. Turnover Rate Matrix */}
      {!hideSections.includes('turnover_rate') && (isAdmin || canSeeQuestion('brsr_a_turnover_rate')) && (
      <Collapsible open={openSections.turnover} onOpenChange={() => toggleSection('turnover')} className="border rounded-lg bg-white">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-3 hover:bg-stone-50">
            <div className="flex items-center gap-2">
              {openSections.turnover ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <TrendingDown className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">Turnover Rate (%) - Last 3 Financial Years</span>
            </div>
            <Badge variant="outline" className="text-xs">3 Years</Badge>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="p-4 pt-0 border-t">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-100">
                  <TableHead className="text-xs font-semibold" rowSpan={2}>Category</TableHead>
                  <TableHead className="text-xs text-center font-semibold border-l" colSpan={2}>Current FY ({reportingYear})</TableHead>
                  <TableHead className="text-xs text-center font-semibold border-l" colSpan={2}>Previous FY ({prevFY})</TableHead>
                  <TableHead className="text-xs text-center font-semibold border-l" colSpan={2}>Prior FY ({priorFY})</TableHead>
                </TableRow>
                <TableRow className="bg-stone-50">
                  <TableHead className="text-xs text-center border-l">Male</TableHead>
                  <TableHead className="text-xs text-center">Female</TableHead>
                  <TableHead className="text-xs text-center border-l">Male</TableHead>
                  <TableHead className="text-xs text-center">Female</TableHead>
                  <TableHead className="text-xs text-center border-l">Male</TableHead>
                  <TableHead className="text-xs text-center">Female</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Permanent Employees Row */}
                <TableRow>
                  <TableCell className="text-xs font-medium">Permanent Employees</TableCell>
                  {/* Current - Editable */}
                  <TableCell className="border-l">
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={turnoverRate.permanent_employees_male}
                        onChange={(e) => setTurnoverRate(p => ({ ...p, permanent_employees_male: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{turnoverRate.permanent_employees_male}%</span>}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={turnoverRate.permanent_employees_female}
                        onChange={(e) => setTurnoverRate(p => ({ ...p, permanent_employees_female: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{turnoverRate.permanent_employees_female}%</span>}
                  </TableCell>
                  {/* Previous - Editable */}
                  <TableCell className="border-l bg-stone-50">
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={prevYearTurnover.permanent_employees_male}
                        onChange={(e) => setPrevYearTurnover(p => ({ ...p, permanent_employees_male: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{prevYearTurnover.permanent_employees_male}%</span>}
                  </TableCell>
                  <TableCell className="bg-stone-50">
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={prevYearTurnover.permanent_employees_female}
                        onChange={(e) => setPrevYearTurnover(p => ({ ...p, permanent_employees_female: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{prevYearTurnover.permanent_employees_female}%</span>}
                  </TableCell>
                  {/* Prior - Editable */}
                  <TableCell className="border-l bg-stone-100">
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={priorYearTurnover.permanent_employees_male}
                        onChange={(e) => setPriorYearTurnover(p => ({ ...p, permanent_employees_male: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{priorYearTurnover.permanent_employees_male}%</span>}
                  </TableCell>
                  <TableCell className="bg-stone-100">
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={priorYearTurnover.permanent_employees_female}
                        onChange={(e) => setPriorYearTurnover(p => ({ ...p, permanent_employees_female: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{priorYearTurnover.permanent_employees_female}%</span>}
                  </TableCell>
                </TableRow>
                {/* Permanent Workers Row */}
                <TableRow>
                  <TableCell className="text-xs font-medium">Permanent Workers</TableCell>
                  {/* Current - Editable */}
                  <TableCell className="border-l">
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={turnoverRate.permanent_workers_male}
                        onChange={(e) => setTurnoverRate(p => ({ ...p, permanent_workers_male: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{turnoverRate.permanent_workers_male}%</span>}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={turnoverRate.permanent_workers_female}
                        onChange={(e) => setTurnoverRate(p => ({ ...p, permanent_workers_female: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{turnoverRate.permanent_workers_female}%</span>}
                  </TableCell>
                  {/* Previous - Editable */}
                  <TableCell className="border-l bg-stone-50">
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={prevYearTurnover.permanent_workers_male}
                        onChange={(e) => setPrevYearTurnover(p => ({ ...p, permanent_workers_male: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{prevYearTurnover.permanent_workers_male}%</span>}
                  </TableCell>
                  <TableCell className="bg-stone-50">
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={prevYearTurnover.permanent_workers_female}
                        onChange={(e) => setPrevYearTurnover(p => ({ ...p, permanent_workers_female: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{prevYearTurnover.permanent_workers_female}%</span>}
                  </TableCell>
                  {/* Prior - Editable */}
                  <TableCell className="border-l bg-stone-100">
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={priorYearTurnover.permanent_workers_male}
                        onChange={(e) => setPriorYearTurnover(p => ({ ...p, permanent_workers_male: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{priorYearTurnover.permanent_workers_male}%</span>}
                  </TableCell>
                  <TableCell className="bg-stone-100">
                    {isEditing ? (
                      <Input type="number" min="0" max="100" step="0.1" value={priorYearTurnover.permanent_workers_female}
                        onChange={(e) => setPriorYearTurnover(p => ({ ...p, permanent_workers_female: parseFloat(e.target.value) || 0 }))}
                        className="h-7 text-xs text-center w-16" />
                    ) : <span className="text-xs">{priorYearTurnover.permanent_workers_female}%</span>}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-text-muted mt-2">* All 3 years are editable. Saving will update each year&apos;s document separately.</p>
        </CollapsibleContent>
      </Collapsible>
      )}

      {/* 6. Complaints & Grievances */}
      {!hideSections.includes('complaints_grievances') && (isAdmin || canSeeQuestion('brsr_a_complaints_grievances')) && (
      <Collapsible open={openSections.complaints} onOpenChange={() => toggleSection('complaints')} className="border rounded-lg bg-white">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-3 hover:bg-stone-50">
            <div className="flex items-center gap-2">
              {openSections.complaints ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <MessageSquareWarning className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">Complaints & Grievances</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {complaintsGrievances.reduce((sum, c) => sum + (c.current_fy_filed || 0), 0)} Filed
            </Badge>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="p-4 pt-0 border-t">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="text-xs min-w-[150px]">Category</TableHead>
                  <TableHead className="text-xs text-center min-w-[80px]">Mechanism</TableHead>
                  <TableHead className="text-xs min-w-[150px]">Policy Web-link</TableHead>
                  <TableHead className="text-xs text-center">Filed</TableHead>
                  <TableHead className="text-xs text-center">Pending</TableHead>
                  <TableHead className="text-xs min-w-[120px]">Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {complaintsGrievances.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-xs font-medium">{row.category}</TableCell>
                    <TableCell className="text-center">
                      {isEditing ? (
                        <Switch
                          checked={row.has_grievance_mechanism}
                          onCheckedChange={(v) => {
                            const updated = [...complaintsGrievances];
                            updated[idx].has_grievance_mechanism = v;
                            if (!v) updated[idx].policy_weblink = "";
                            setComplaintsGrievances(updated);
                          }}
                        />
                      ) : (
                        <Badge variant="outline" className={`text-xs ${row.has_grievance_mechanism ? 'bg-green-50 text-green-700' : ''}`}>
                          {row.has_grievance_mechanism ? 'Yes' : 'No'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={row.policy_weblink}
                          onChange={(e) => {
                            const updated = [...complaintsGrievances];
                            updated[idx].policy_weblink = e.target.value;
                            setComplaintsGrievances(updated);
                          }}
                          placeholder={row.has_grievance_mechanism ? "Required" : "N/A"}
                          disabled={!row.has_grievance_mechanism}
                          className={`h-7 text-xs ${row.has_grievance_mechanism && !row.policy_weblink ? 'border-red-300' : ''}`}
                        />
                      ) : (
                        <span className="text-xs text-blue-600 underline">{row.policy_weblink || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input type="number" min="0" value={row.current_fy_filed}
                          onChange={(e) => {
                            const updated = [...complaintsGrievances];
                            updated[idx].current_fy_filed = parseInt(e.target.value) || 0;
                            setComplaintsGrievances(updated);
                          }}
                          className="h-7 text-xs text-center w-16" />
                      ) : <span className="text-xs">{row.current_fy_filed}</span>}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input type="number" min="0" value={row.current_fy_pending}
                          onChange={(e) => {
                            const updated = [...complaintsGrievances];
                            updated[idx].current_fy_pending = parseInt(e.target.value) || 0;
                            setComplaintsGrievances(updated);
                          }}
                          className="h-7 text-xs text-center w-16" />
                      ) : <span className="text-xs">{row.current_fy_pending}</span>}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input value={row.current_fy_remarks}
                          onChange={(e) => {
                            const updated = [...complaintsGrievances];
                            updated[idx].current_fy_remarks = e.target.value;
                            setComplaintsGrievances(updated);
                          }}
                          className="h-7 text-xs" placeholder="Remarks" />
                      ) : <span className="text-xs">{row.current_fy_remarks || '-'}</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-text-muted mt-2">* Web-link is mandatory when Grievance Mechanism is enabled</p>
        </CollapsibleContent>
      </Collapsible>
      )}

      {/* 7. Material Responsible Business Conduct Issues */}
      {(isAdmin || canSeeQuestion('brsr_a_material_issues')) && (
      <Collapsible open={openSections.materialIssues} onOpenChange={() => toggleSection('materialIssues')} className="border rounded-lg bg-white">
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-3 hover:bg-stone-50">
            <div className="flex items-center gap-2">
              {openSections.materialIssues ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <AlertTriangle className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm">Material Business Conduct Issues</span>
            </div>
            <Badge variant="outline" className="text-xs">{materialIssues.filter(i => i.issue_identified).length} Issues</Badge>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="p-4 pt-0 border-t">
          <div className="space-y-3">
            {materialIssues.map((issue, idx) => (
              <div key={idx} className="border rounded-lg p-3 bg-stone-50">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-xs font-medium text-stone-500">Issue #{idx + 1}</span>
                  {isEditing && canEditQuestion('brsr_a_material_issues') && materialIssues.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => setMaterialIssues(materialIssues.filter((_, i) => i !== idx))}
                      className="h-6 w-6 p-0 text-red-500"><Trash2 className="w-3 h-3" /></Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Material Issue Identified</Label>
                    {isEditing && canEditQuestion('brsr_a_material_issues') ? (
                      <Textarea value={issue.issue_identified} onChange={(e) => {
                        const updated = [...materialIssues]; updated[idx].issue_identified = e.target.value; setMaterialIssues(updated);
                      }} className="text-xs mt-1" rows={2} placeholder="Describe the material issue" />
                    ) : <p className="text-xs mt-1">{issue.issue_identified || '-'}</p>}
                  </div>
                  <div>
                    <Label className="text-xs">Risk or Opportunity</Label>
                    {isEditing ? (
                      <Select value={issue.risk_or_opportunity} onValueChange={(v) => {
                        const updated = [...materialIssues]; updated[idx].risk_or_opportunity = v; setMaterialIssues(updated);
                      }}>
                        <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Risk">Risk</SelectItem>
                          <SelectItem value="Opportunity">Opportunity</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className={`text-xs mt-1 ${issue.risk_or_opportunity === 'Risk' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                        {issue.risk_or_opportunity}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Rationale for Identification</Label>
                    {isEditing ? (
                      <Textarea value={issue.rationale} onChange={(e) => {
                        const updated = [...materialIssues]; updated[idx].rationale = e.target.value; setMaterialIssues(updated);
                      }} className="text-xs mt-1" rows={2} placeholder="Why was this identified?" />
                    ) : <p className="text-xs mt-1">{issue.rationale || '-'}</p>}
                  </div>
                  <div>
                    <Label className="text-xs">Mitigation Approach {issue.risk_or_opportunity === 'Risk' && <span className="text-red-500">*</span>}</Label>
                    {isEditing ? (
                      <Textarea value={issue.mitigation_approach} onChange={(e) => {
                        const updated = [...materialIssues]; updated[idx].mitigation_approach = e.target.value; setMaterialIssues(updated);
                      }} className="text-xs mt-1" rows={2} placeholder={issue.risk_or_opportunity === 'Risk' ? 'Required for risks' : 'Optional'} />
                    ) : <p className="text-xs mt-1">{issue.mitigation_approach || '-'}</p>}
                  </div>
                  <div>
                    <Label className="text-xs">Financial Implication</Label>
                    {isEditing ? (
                      <Select value={issue.financial_implication} onValueChange={(v) => {
                        const updated = [...materialIssues]; updated[idx].financial_implication = v; setMaterialIssues(updated);
                      }}>
                        <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Positive">Positive</SelectItem>
                          <SelectItem value="Negative">Negative</SelectItem>
                          <SelectItem value="Neutral">Neutral</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className={`text-xs mt-1 ${issue.financial_implication === 'Positive' ? 'bg-green-50 text-green-700' : issue.financial_implication === 'Negative' ? 'bg-red-50 text-red-700' : ''}`}>
                        {issue.financial_implication}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Financial Details</Label>
                    {isEditing ? (
                      <Textarea value={issue.financial_details} onChange={(e) => {
                        const updated = [...materialIssues]; updated[idx].financial_details = e.target.value; setMaterialIssues(updated);
                      }} className="text-xs mt-1" rows={2} placeholder="Describe financial implications" />
                    ) : <p className="text-xs mt-1">{issue.financial_details || '-'}</p>}
                  </div>
                </div>
              </div>
            ))}
            {isEditing && canEditQuestion('brsr_a_material_issues') && (
              <Button variant="outline" size="sm" onClick={() => setMaterialIssues([...materialIssues, { ...DEFAULT_MATERIAL_ISSUE_ROW }])}>
                <Plus className="w-3 h-3 mr-1" /> Add Issue
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
      )}

      {/* Single Save Button for All Sections */}
      {isEditing && (
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={saveAllYearlyData} disabled={saving} className="bg-primary hover:bg-primary/90 text-white" data-testid="save-all-yearly-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save All Yearly Data</>}
          </Button>
        </div>
      )}

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Historical Yearly Data</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {historicalData.length === 0 ? (
              <p className="text-sm text-text-muted text-center py-8">No historical data available</p>
            ) : (
              historicalData.map((yearData) => (
                <div key={yearData.reporting_year} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{yearData.reporting_year}</h4>
                    <Button variant="outline" size="sm" onClick={() => { setReportingYear(yearData.reporting_year); setShowHistoryModal(false); }}>
                      Load & Edit
                    </Button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div className="bg-stone-50 p-2 rounded">Employees: {(yearData.employee_worker_details?.permanent_male_employees || 0) + (yearData.employee_worker_details?.permanent_female_employees || 0)}</div>
                    <div className="bg-stone-50 p-2 rounded">CSR: {yearData.csr_applicability?.is_applicable ? 'Yes' : 'No'}</div>
                    <div className="bg-stone-50 p-2 rounded">Entities: {yearData.holding_subsidiary_entities?.length || 0}</div>
                    <div className="bg-stone-50 p-2 rounded">Women on Board: {yearData.women_representation?.reduce((s, r) => s + (r.number_of_females || 0), 0) || 0}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
